import React from 'react';
import {
  AlertTriangle,
  Clock,
  DollarSign,
  Navigation,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { Card } from '../UI/card';
import { Badge } from '../UI/badge';
import LandingShowcaseSection from './LandingShowcaseSection';
import {
  LANDING_RISK_DEMO,
  LANDING_ROUTE_RISK_DEMO,
  getRiskBarClass,
  getRiskSoftClass,
} from './landingDemoData';

function RiskScoreBar({ labelKey, score }) {
  const { t } = useLocale();
  const barClass = getRiskBarClass(score);

  return (
    <div className="rounded-lg border border-border-light bg-canvas-soft p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-text-secondary">{t(labelKey)}</span>
        <span className="text-sm font-semibold tabular-nums text-text-primary">{score}/100</span>
      </div>
      <div className={themeClasses.chart.barTrack}>
        <div className={`h-2 rounded-full ${barClass}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function LandingCompanyRiskCard() {
  const { t } = useLocale();
  const { overallScore, supplyChain, industry, newsSentiment, confidence } = LANDING_RISK_DEMO;
  const overallClass = getRiskSoftClass(overallScore);
  const barClass = getRiskBarClass(overallScore);

  return (
    <Card className={themeClasses.landing.showcaseCard}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{t('landingRiskCompanyTitle')}</h3>
          <p className="mt-1 text-sm text-text-secondary">{t('landingDemoCompanyName')}</p>
        </div>
        <Badge variant="outline" className={overallClass}>
          {t('landingDemoRiskLevel')}
        </Badge>
      </div>

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-text-secondary">
            {t('landingRiskOverallScore')}
          </span>
          <span className="text-lg font-bold tabular-nums text-text-primary">
            {overallScore}/100
          </span>
        </div>
        <div className={themeClasses.chart.barTrack}>
          <div className={`h-2 rounded-full ${barClass}`} style={{ width: `${overallScore}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <RiskScoreBar labelKey="landingRiskSupplyChain" score={supplyChain} />
        <RiskScoreBar labelKey="landingRiskIndustry" score={industry} />
        <RiskScoreBar labelKey="landingRiskNewsSentiment" score={newsSentiment} />
      </div>

      <div className="mt-6 flex items-center gap-2 rounded-lg border border-border-light bg-background-secondary px-4 py-3">
        <TrendingUp className="size-4 text-text-tertiary" aria-hidden />
        <span className="text-sm text-text-secondary">{t('landingRiskConfidence')}</span>
        <span className="ml-auto text-sm font-semibold tabular-nums text-text-primary">
          {confidence}%
        </span>
      </div>
    </Card>
  );
}

function LandingRouteRiskCard() {
  const { t } = useLocale();
  const { riskScore } = LANDING_ROUTE_RISK_DEMO;
  const riskClass = getRiskSoftClass(riskScore);

  return (
    <Card className={themeClasses.landing.showcaseCard}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border-light bg-canvas-soft">
            <Navigation className="size-5 text-brand-600" aria-hidden />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{t('landingRiskRouteTitle')}</h3>
            <p className="mt-1 text-sm text-text-secondary">{t('landingDemoRouteName')}</p>
          </div>
        </div>
        <Badge variant="outline" className={riskClass}>
          <AlertTriangle className="mr-1 size-3" aria-hidden />
          {riskScore}/100
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border-light bg-canvas-soft p-4">
          <div className="mb-2 flex items-center gap-2 text-text-tertiary">
            <Truck className="size-4" aria-hidden />
            <span className="text-xs font-medium uppercase tracking-wide">{t('landingRiskDistance')}</span>
          </div>
          <p className="text-lg font-semibold tabular-nums text-text-primary">
            {t('landingDemoRouteDistance')}
          </p>
        </div>
        <div className="rounded-lg border border-border-light bg-canvas-soft p-4">
          <div className="mb-2 flex items-center gap-2 text-text-tertiary">
            <Clock className="size-4" aria-hidden />
            <span className="text-xs font-medium uppercase tracking-wide">{t('landingRiskDuration')}</span>
          </div>
          <p className="text-lg font-semibold tabular-nums text-text-primary">
            {t('landingDemoRouteDuration')}
          </p>
        </div>
        <div className="rounded-lg border border-border-light bg-canvas-soft p-4">
          <div className="mb-2 flex items-center gap-2 text-text-tertiary">
            <DollarSign className="size-4" aria-hidden />
            <span className="text-xs font-medium uppercase tracking-wide">{t('landingRiskCost')}</span>
          </div>
          <p className="text-lg font-semibold tabular-nums text-text-primary">
            {t('landingDemoRouteCost')}
          </p>
        </div>
      </div>

      <p className="mt-6 text-sm leading-relaxed text-text-secondary">{t('landingRiskRouteHint')}</p>
    </Card>
  );
}

export default function LandingRiskShowcase() {
  return (
    <LandingShowcaseSection
      eyebrowKey="landingRiskEyebrow"
      titleKey="landingRiskTitle"
      subtitleKey="landingRiskSubtitle"
      background="secondary"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <LandingCompanyRiskCard />
        <LandingRouteRiskCard />
      </div>
    </LandingShowcaseSection>
  );
}
